'use client';

import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '@/server/callers/client';
import { trackEvent } from '@/utils/analytics';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const createContactMutation = api.contact.create.useMutation({
    onSuccess: () => {
      // Reset form
      setFormData({ name: '', email: '', message: '' });

      // Track successful submission
      trackEvent({
        action: 'contact_form_success',
        category: 'forms',
        label: 'Contact Form Success',
        value: 1,
      });

      toast.success('Thank you for your message! We will get back to you soon.');
    },
    onError: (error) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Track form submission
    trackEvent({
      action: 'contact_form_submit',
      category: 'forms',
      label: 'Contact Form',
      value: 1,
    });

    // Submit form using tRPC mutation
    createContactMutation.mutate(formData);
  };

  return (
    <section data-aos="fade-up" className="w-full bg-white py-8 md:py-16">
      <div className="container max-w-6xl mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left side - Image with contact info */}
          <div className="w-full lg:w-1/2 relative rounded-lg overflow-hidden h-[450px] md:h-[600px]">
            <img
              src="/images/contact.jpg"
              alt="King Shalmaneser III of Assyria In the Iraqi museum"
              className="w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-black opacity-20 z-10" />
          </div>

          {/* Right side - Form */}
          <div className="w-full lg:w-1/2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  minLength={2}
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email*
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Hello@gmail.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Message*
                </label>
                <textarea
                  id="message"
                  name="message"
                  minLength={10}
                  placeholder="Write Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={createContactMutation.isPending}
                className="bg-accent font-medium rounded-lg w-fit px-12 py-3 hover:bg-accent transition-colors disabled:bg-blue-400"
              >
                Contact Us
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
